import { create } from "zustand";
import i18n from "@/i18n/i18n";

const STORAGE_KEY_PREFIX = "ld_notifications_";

// ─── Types ────────────────────────────────────────────────────────────────────

export type NotificationDisplayType = "ROUTE" | "MODAL";

export type NotificationIconType = "study" | "streak" | "achievement" | "shop" | "reminder";

/** Notification kind: only type + payload are stored. Text is computed at render time. */
export type NotificationKind =
  | "WORKOUT_COMPLETED"
  | "RANK_UP"
  | "STREAK_MILESTONE"
  | "STUDY_REWARD"
  | "STREAK_WARNING"
  | "SPACED_REPETITION"
  | "MISTAKE_REVIEW"
  | "WEEKLY_INSIGHT"
  | "MICRO_LEARNING"
  | "TEST_COMPLETE"
  | "MEDAL_TIME_MASTER"
  | "MEDAL_APP_CHAMPION"
  | "DUMMY_RETURN"
  | "DUMMY_STREAK"
  | "DUMMY_LEVEL"
  | "DUMMY_REPEAT"
  | "DUMMY_MICRO";

export interface Notification {
  id: string;
  /** Determines which i18n template to use at render time. Omitted for legacy persisted items. */
  kind?: NotificationKind;
  /** Data for interpolation (crystals, xp, rankId, deckName, etc.). Never store translated strings. */
  payload?: Record<string, string | number>;
  displayType: NotificationDisplayType;
  target: string;
  iconType: NotificationIconType;
  isRead: boolean;
  createdAt: Date;
  timeAgo?: string;
  /** Legacy: fallback when kind is missing (old storage). */
  title?: string;
  actionText?: string;
}

export type AddNotificationInput = {
  kind: NotificationKind;
  payload?: Record<string, string | number>;
  displayType: NotificationDisplayType;
  target: string;
  iconType: NotificationIconType;
};

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  /** Current user ID for scoped storage. Set by hydrateForUser. */
  currentUserId: string | null;
}

interface NotificationActions {
  addNotification: (notification: AddNotificationInput) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  /** Load notifications for this user. Call when user changes. New users get []. */
  hydrateForUser: (userId: string | null) => void;
}

type NotificationStore = NotificationState & NotificationActions;

// ─── Helpers ───────────────────────────────────────────────────────────────────

function generateId(): string {
  return `notif_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function computeUnreadCount(notifications: Notification[]): number {
  return notifications.filter((n) => !n.isRead).length;
}

/** Format relative time in the current locale. Exported for use in NotificationsPopover. */
export function formatTimeAgo(date: Date): string {
  const t = i18n.t.bind(i18n);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffMins < 1) return t("timeAgo.justNow");
  if (diffMins < 60) return t("timeAgo.minutesAgo", { count: diffMins });
  if (diffHours < 24) return t("timeAgo.hoursAgo", { count: diffHours });
  if (diffDays < 7) return t("timeAgo.daysAgo", { count: diffDays });
  if (diffWeeks < 4) return t("timeAgo.weeksAgo", { count: diffWeeks });
  return t("timeAgo.longAgo");
}

// ─── Persistence (user-scoped to prevent cross-user data leaks) ─────────────────

function getStorageKey(userId: string | null): string {
  return `${STORAGE_KEY_PREFIX}${userId || "anon"}`;
}

function loadFromStorage(userId: string | null): Notification[] | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Array<{ createdAt: string } & Omit<Notification, "createdAt">>;
    if (!Array.isArray(parsed)) return null;
    return parsed.map((n) => {
      const base = { ...n, createdAt: new Date(n.createdAt) } as Notification;
      if (!base.displayType && (n as { type?: string }).type) {
        base.displayType = (n as { type: NotificationDisplayType }).type;
      }
      return base;
    }) as Notification[];
  } catch {
    return null;
  }
}

function saveToStorage(userId: string | null, notifications: Notification[]) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(notifications));
  } catch {}
}

/** New users get empty notifications. No global/test data. */
function getInitialNotifications(): Notification[] {
  return [];
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useNotificationStore = create<NotificationStore>((set, get) => {
  const initial = getInitialNotifications();
  return {
    notifications: initial,
    unreadCount: computeUnreadCount(initial),
    currentUserId: null,

    hydrateForUser: (userId) => {
      const loaded = loadFromStorage(userId) ?? [];
      set({
        notifications: loaded,
        unreadCount: computeUnreadCount(loaded),
        currentUserId: userId,
      });
    },

    addNotification: (notification) => {
      const createdAt = new Date();
      const newNotif: Notification = {
        ...notification,
        id: generateId(),
        isRead: false,
        createdAt,
      };
      set((prev) => {
        const notifications = [newNotif, ...prev.notifications];
        saveToStorage(prev.currentUserId, notifications);
        return {
          notifications,
          unreadCount: computeUnreadCount(notifications),
        };
      });
    },

    markAsRead: (id) => {
      set((prev) => {
        const notifications = prev.notifications.map((n) =>
          n.id === id ? { ...n, isRead: true } : n
        );
        saveToStorage(prev.currentUserId, notifications);
        return {
          notifications,
          unreadCount: computeUnreadCount(notifications),
        };
      });
    },

    markAllAsRead: () => {
      set((prev) => {
        const notifications = prev.notifications.map((n) => ({ ...n, isRead: true }));
        saveToStorage(prev.currentUserId, notifications);
        return {
          notifications,
          unreadCount: 0,
        };
      });
    },

    removeNotification: (id) => {
      set((prev) => {
        const notifications = prev.notifications.filter((n) => n.id !== id);
        saveToStorage(prev.currentUserId, notifications);
        return {
          notifications,
          unreadCount: computeUnreadCount(notifications),
        };
      });
    },
  };
});

// ─── Trigger helpers (store only kind + payload; no translated strings) ─────────

function ensureHydratedForUser(userId?: string | null): void {
  if (userId === undefined) return;
  const state = useNotificationStore.getState();
  if (state.currentUserId !== userId) {
    state.hydrateForUser(userId);
  }
}

export function triggerStudyReward(crystals: number): void {
  useNotificationStore.getState().addNotification({
    kind: "STUDY_REWARD",
    payload: { crystals },
    displayType: "MODAL",
    target: "shop",
    iconType: "shop",
  });
}

export function triggerStreakWarning(): void {
  useNotificationStore.getState().addNotification({
    kind: "STREAK_WARNING",
    displayType: "ROUTE",
    target: "/library",
    iconType: "streak",
  });
}

export function triggerRankUp(rankId: string, userId?: string | null): void {
  ensureHydratedForUser(userId);
  useNotificationStore.getState().addNotification({
    kind: "RANK_UP",
    payload: { rankId },
    displayType: "ROUTE",
    target: "/gamification?tab=titles",
    iconType: "achievement",
  });
}

export function triggerStreakMilestone(count: number, userId?: string | null): void {
  ensureHydratedForUser(userId);
  useNotificationStore.getState().addNotification({
    kind: "STREAK_MILESTONE",
    payload: { count },
    displayType: "ROUTE",
    target: "/gamification?tab=streaks",
    iconType: "achievement",
  });
}

export function triggerSpacedRepetition(deckName: string, targetUrl: string): void {
  useNotificationStore.getState().addNotification({
    kind: "SPACED_REPETITION",
    payload: { deckName },
    displayType: "ROUTE",
    target: targetUrl,
    iconType: "reminder",
  });
}

export function triggerMistakeReview(deckName: string, targetUrl: string): void {
  useNotificationStore.getState().addNotification({
    kind: "MISTAKE_REVIEW",
    payload: { deckName },
    displayType: "ROUTE",
    target: targetUrl,
    iconType: "study",
  });
}

export function triggerWeeklyInsight(wordsCount: number): void {
  useNotificationStore.getState().addNotification({
    kind: "WEEKLY_INSIGHT",
    payload: { wordsCount },
    displayType: "ROUTE",
    target: "/library",
    iconType: "streak",
  });
}

export function triggerMicroLearning(): void {
  useNotificationStore.getState().addNotification({
    kind: "MICRO_LEARNING",
    displayType: "ROUTE",
    target: "/library",
    iconType: "reminder",
  });
}
